<template>
  <div class="kb-manager">
    <div class="kb-header">
      <div>
        <h3>Bases de Conocimiento</h3>
        <p class="kb-desc">
          Sube documentos para que el agente los use como contexto en sus respuestas (RAG).
          Los documentos se dividen en fragmentos y se indexan semánticamente.
        </p>
      </div>
      <Button label="Nueva KB" icon="pi pi-plus" size="small" @click="showCreateDialog = true" />
    </div>

    <!-- Lista de KBs -->
    <div v-if="loading" class="loading-msg">
      <i class="pi pi-spin pi-spinner" /> Cargando...
    </div>

    <div v-else-if="knowledgeBases.length === 0" class="empty-kb">
      <i class="pi pi-database" style="font-size:2rem;color:#d1d5db" />
      <p>No hay bases de conocimiento para este agente.</p>
      <Button label="Crear primera KB" icon="pi pi-plus" outlined @click="showCreateDialog = true" />
    </div>

    <div v-else class="kb-list">
      <div v-for="kb in knowledgeBases" :key="kb.id" class="kb-card" :class="{ inactive: !kb.is_active }">
        <div class="kb-card-header">
          <div class="kb-title">
            <i class="pi pi-database" />
            <h4>{{ kb.name }}</h4>
            <Tag v-if="!kb.is_active" value="Inactiva" severity="secondary" size="small" />
          </div>
          <div class="kb-actions">
            <Button
              :icon="kb.is_active ? 'pi pi-eye-slash' : 'pi pi-eye'"
              text rounded size="small"
              :severity="kb.is_active ? 'warning' : 'success'"
              @click="toggleActive(kb)"
            />
            <Button icon="pi pi-trash" text rounded size="small" severity="danger" @click="confirmDelete(kb)" />
          </div>
        </div>
        <p v-if="kb.description" class="kb-description">{{ kb.description }}</p>
        <div class="kb-stats">
          <span class="stat"><i class="pi pi-file" /> {{ kb.document_count }} documentos</span>
          <span class="stat"><i class="pi pi-bars" /> {{ kb.chunk_count }} fragmentos</span>
        </div>

        <!-- Documentos de la KB -->
        <div class="kb-documents">
          <div class="doc-upload-row">
            <FileUpload
              :auto="false"
              :multiple="false"
              :customUpload="true"
              @uploader="(e) => uploadDocument(kb.id, e)"
              :accept="'.pdf,.txt,.md,.csv,.xlsx,.xls'"
              :maxFileSize="10485760"
              chooseLabel="Subir documento"
              class="doc-upload"
            />
            <span class="upload-hint">PDF, TXT, MD, CSV, XLSX (máx 10MB)</span>
          </div>

          <DataTable
            v-if="kbDocuments[kb.id]?.length"
            :value="kbDocuments[kb.id]"
            size="small"
            responsiveLayout="scroll"
          >
            <Column field="filename" header="Archivo" />
            <Column field="status" header="Estado" style="width: 120px">
              <template #body="{ data }">
                <Tag
                  :value="statusLabel(data.status)"
                  :severity="statusSeverity(data.status)"
                  size="small"
                />
              </template>
            </Column>
            <Column field="chunk_count" header="Chunks" style="width: 80px" />
            <Column header="" style="width: 50px">
              <template #body="{ data }">
                <Button icon="pi pi-trash" text size="small" severity="danger" @click="deleteDocument(data)" />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Dialog crear KB -->
    <Dialog v-model:visible="showCreateDialog" header="Nueva base de conocimiento" modal :style="{ width: '450px' }">
      <div class="create-form">
        <div class="field">
          <label>Nombre <span class="required">*</span></label>
          <InputText v-model="newKb.name" placeholder="Ej: Normativa Tributaria Colombia" class="w-full" />
        </div>
        <div class="field">
          <label>Descripción</label>
          <Textarea v-model="newKb.description" placeholder="Describe el contenido de esta base..." rows="3" class="w-full" autoResize />
        </div>
      </div>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showCreateDialog = false" />
        <Button label="Crear" icon="pi pi-check" :loading="creating" :disabled="!newKb.name.trim()" @click="createKb" />
      </template>
    </Dialog>

    <!-- Dialog confirmar eliminación -->
    <Dialog v-model:visible="showDeleteConfirm" header="Eliminar KB" modal :style="{ width: '380px' }">
      <p>¿Eliminar la base <strong>{{ deletingKb?.name }}</strong> y todos sus documentos?</p>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showDeleteConfirm = false" />
        <Button label="Eliminar" severity="danger" :loading="deleting" @click="executeDelete" />
      </template>
    </Dialog>

    <Toast />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import FileUpload from 'primevue/fileupload';
import Toast from 'primevue/toast';
import api from '../../services/api';

const props = defineProps({
  agentId: { type: Number, required: true },
});

const toast = useToast();
const knowledgeBases = ref([]);
const kbDocuments = ref({});
const loading = ref(false);
const showCreateDialog = ref(false);
const creating = ref(false);
const newKb = ref({ name: '', description: '' });
const showDeleteConfirm = ref(false);
const deletingKb = ref(null);
const deleting = ref(false);
const uploading = ref(false);

const loadKbs = async () => {
  loading.value = true;
  try {
    const res = await api.get(`/admin/agents/${props.agentId}/knowledge-bases`);
    knowledgeBases.value = res.data;
    // Cargar documentos de cada KB
    for (const kb of knowledgeBases.value) {
      await loadDocuments(kb.id);
    }
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  } finally {
    loading.value = false;
  }
};

const loadDocuments = async (kbId) => {
  try {
    const res = await api.get(`/admin/knowledge-bases/${kbId}/documents`);
    kbDocuments.value[kbId] = res.data;
  } catch (e) {
    console.error(e);
  }
};

const createKb = async () => {
  if (!newKb.value.name.trim()) return;
  creating.value = true;
  try {
    await api.post(`/admin/agents/${props.agentId}/knowledge-bases`, {
      name: newKb.value.name.trim(),
      description: newKb.value.description?.trim() || null,
    });
    showCreateDialog.value = false;
    newKb.value = { name: '', description: '' };
    toast.add({ severity: 'success', summary: 'Creada', detail: 'Base de conocimiento creada', life: 2000 });
    await loadKbs();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  } finally {
    creating.value = false;
  }
};

const toggleActive = async (kb) => {
  try {
    await api.put(`/admin/knowledge-bases/${kb.id}`, { is_active: !kb.is_active });
    kb.is_active = !kb.is_active;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cambiar el estado', life: 3000 });
  }
};

const confirmDelete = (kb) => {
  deletingKb.value = kb;
  showDeleteConfirm.value = true;
};

const executeDelete = async () => {
  if (!deletingKb.value) return;
  deleting.value = true;
  try {
    await api.delete(`/admin/knowledge-bases/${deletingKb.value.id}`);
    showDeleteConfirm.value = false;
    toast.add({ severity: 'success', summary: 'Eliminada', detail: 'KB eliminada', life: 2000 });
    await loadKbs();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  } finally {
    deleting.value = false;
  }
};

const uploadDocument = async (kbId, event) => {
  const files = event.files || [event];
  if (!files.length) return;
  uploading.value = true;
  const file = files[0];
  const formData = new FormData();
  formData.append('file', file);
  try {
    const res = await api.post(`/admin/knowledge-bases/${kbId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    toast.add({
      severity: 'success',
      summary: 'Indexado',
      detail: `${file.name}: ${res.data.chunk_count} fragmentos generados`,
      life: 3000,
    });
    await loadDocuments(kbId);
    await loadKbs();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 4000 });
  } finally {
    uploading.value = false;
  }
};

const deleteDocument = async (doc) => {
  try {
    await api.delete(`/admin/kb-documents/${doc.id}`);
    toast.add({ severity: 'success', summary: 'Eliminado', life: 2000 });
    await loadDocuments(doc.knowledge_base_id);
    await loadKbs();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  }
};

const statusLabel = (status) => {
  const labels = { pending: 'Pendiente', indexing: 'Indexando...', indexed: 'Indexado', failed: 'Error' };
  return labels[status] || status;
};

const statusSeverity = (status) => {
  const severities = { pending: 'warning', indexing: 'info', indexed: 'success', failed: 'danger' };
  return severities[status] || 'secondary';
};

watch(() => props.agentId, () => {
  if (props.agentId) loadKbs();
});

onMounted(() => {
  if (props.agentId) loadKbs();
});
</script>

<style scoped>
.kb-manager { display: flex; flex-direction: column; gap: 1rem; }

.kb-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.kb-header h3 { margin: 0; font-size: 1rem; color: #1e293b; }
.kb-desc { margin: 0.25rem 0 0; font-size: 0.82rem; color: #6b7280; max-width: 500px; }

.loading-msg, .empty-kb { text-align: center; padding: 2rem; color: #9ca3af; display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }

.kb-list { display: flex; flex-direction: column; gap: 1rem; }
.kb-card {
  border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden;
  transition: box-shadow 0.15s;
}
.kb-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.kb-card.inactive { opacity: 0.6; }

.kb-card-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.75rem 1rem; background: #f8fafc; border-bottom: 1px solid #f1f5f9;
}
.kb-title { display: flex; align-items: center; gap: 0.5rem; }
.kb-title h4 { margin: 0; font-size: 0.95rem; color: #1e293b; }
.kb-actions { display: flex; gap: 0.1rem; }
.kb-description { margin: 0.5rem 1rem; font-size: 0.82rem; color: #6b7280; }

.kb-stats {
  display: flex; gap: 1.5rem; padding: 0.5rem 1rem;
  font-size: 0.8rem; color: #6b7280;
}
.stat { display: flex; align-items: center; gap: 0.3rem; }

.kb-documents { padding: 0.75rem 1rem; border-top: 1px solid #f1f5f9; }
.doc-upload-row { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; flex-wrap: wrap; }
.upload-hint { font-size: 0.72rem; color: #9ca3af; }

.create-form { display: flex; flex-direction: column; gap: 1rem; }
.field { display: flex; flex-direction: column; gap: 0.3rem; }
.field label { font-weight: 600; font-size: 0.875rem; color: #374151; }
.required { color: #ef4444; }
.w-full { width: 100%; }
</style>
